/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import React from 'react';
import { Copy, ExitDomain } from '@opengeoweb/theme';
import type { InfoDialogContactsProps } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

const DiscoveryCatalogueInfo: React.FC<InfoDialogContactsProps> = ({
  addresses,
  contactInstructions,
  emails,
  hoursOfService,
  links,
  name,
  organization,
  phones,
  roles,
}: InfoDialogContactsProps) => {
  const { t } = useDataExplorerTranslation();
  const address = addresses?.[0]?.deliveryPoint?.[0];
  const email = emails?.[0]?.value;
  const link = links?.[0]?.href;
  const role = roles?.[0];
  const phone = phones?.[0]?.value;
  // Add colon between hours and minutes in the format HHMMh
  const formatHoursOfService = (hoursOfService: string): string => {
    return hoursOfService.replace(/(\d{2})(\d{2})h/g, '$1:$2');
  };

  return (
    <Grid
      container
      data-testid="InfoDialogContactsInfo"
      spacing={2}
      sx={{ flexDirection: 'column' }}
    >
      {!!name && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-name')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{name}</Typography>
          </Grid>
        </Grid>
      )}
      {!!address && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-address')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{address}</Typography>
          </Grid>
        </Grid>
      )}
      {!!email && (
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Grid
            container
            size={10}
            spacing={0}
            sx={{ flexDirection: 'column' }}
          >
            <Grid>
              <Typography color="textSecondary" variant="caption">
                {t('contacts-email')}
              </Typography>
            </Grid>
            <Grid>
              <Typography>{email}</Typography>
            </Grid>
          </Grid>
          <Grid
            container
            size={2}
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <Grid>
              <Tooltip title={t('Copy the link')}>
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(email).catch((error) => {
                      console.error('Failed to copy text to clipboard:', error);
                    });
                  }}
                  style={{ borderRadius: 50 }}
                >
                  <Copy />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!!link && (
        <Grid
          container
          direction="row"
          size={12}
          spacing={0}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Grid container sx={{ flexDirection: 'column' }}>
            <Grid>
              <Typography color="textSecondary" variant="caption">
                {t('contacts-web')}
              </Typography>
            </Grid>
            <Grid>
              <Typography>{link}</Typography>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <MuiLink href={link} rel="noopener" target="_blank">
              <Tooltip title={t('Open')}>
                <IconButton style={{ borderRadius: 50, color: '#186DFF' }}>
                  <ExitDomain />
                </IconButton>
              </Tooltip>
            </MuiLink>
            <Grid>
              <Tooltip title={t('Copy the link')}>
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(link).catch((error) => {
                      console.error('Failed to copy text to clipboard:', error);
                    });
                  }}
                  style={{ borderRadius: 50 }}
                >
                  <Copy />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!!role && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-role')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!organization && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-organisation')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{organization}</Typography>
          </Grid>
        </Grid>
      )}
      {!!hoursOfService && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-hours-of-service')}
            </Typography>
          </Grid>
          <Grid>
            <Typography data-testid="contactsHoursOfService">
              {formatHoursOfService(hoursOfService)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!contactInstructions && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-contact-instructions')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>
              {contactInstructions.charAt(0).toUpperCase() +
                contactInstructions.slice(1)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!phone && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-telephone')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{phone}</Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default DiscoveryCatalogueInfo;
